import React from "react";
import {
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileWordOutlined,
  PlayCircleOutlined,
  AudioOutlined,
  ContactsOutlined,
} from "@ant-design/icons";

// Allowed file types for WhatsApp / MMS. Includes browser aliases (audio/mp3)
// on top of the Twilio MIME names.
export const WHATSAPP_ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "audio/ogg",
  "audio/amr",
  "audio/3gpp",
  "audio/aac",
  "audio/x-aac",
  "audio/mpeg",
  "audio/mp3",
  "audio/x-mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "video/mp4",
  "video/3gpp",
  "video/quicktime",
  "video/webm",
  "text/vcard",
  "text/x-vcard",
];

const EXT_KIND: Record<string, string> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
  gif: "image",
  mp3: "audio",
  mpeg: "audio",
  mpga: "audio",
  wav: "audio",
  ogg: "audio",
  oga: "audio",
  aac: "audio",
  amr: "audio",
  m4a: "audio",
  "3gp": "audio",
  mp4: "video",
  mov: "video",
  webm: "video",
  m4v: "video",
  pdf: "document",
  doc: "document",
  docx: "document",
  ppt: "document",
  pptx: "document",
  xls: "document",
  xlsx: "document",
  vcf: "contact",
};

export const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB

export const extensionOf = (name?: string): string => {
  const value = String(name || "").split("?")[0];
  const dot = value.lastIndexOf(".");
  if (dot < 0) return "";
  return value.slice(dot + 1).toLowerCase();
};

export const kindFromFilename = (name?: string): string => {
  return EXT_KIND[extensionOf(name)] || "";
};

export const kindFromMime = (mimeType?: string): string => {
  const ct = String(mimeType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("audio/")) return "audio";
  if (ct.startsWith("video/")) return "video";
  if (ct === "text/vcard" || ct === "text/x-vcard") return "contact";
  if (ct === "application/pdf" || ct.includes("word") || ct.includes("sheet") || ct.includes("presentation")) {
    return "document";
  }
  if (ct) return "document";
  return "";
};

export const mediaKindOf = (item: {
  kind?: string;
  content_type?: string;
  filename?: string;
  url?: string;
}): string => {
  const fromMime = kindFromMime(item.content_type);
  if (fromMime) return fromMime;
  if (item.kind && item.kind !== "unknown") return item.kind;
  return (
    kindFromFilename(item.filename) ||
    kindFromFilename(item.url) ||
    "document"
  );
};

export const isAllowedWhatsappFile = (file: File): boolean => {
  if (WHATSAPP_ALLOWED_FILE_TYPES.includes(file.type)) return true;
  const kind = kindFromFilename(file.name);
  return Boolean(kind);
};

/**
 * Returns an appropriate icon component based on file MIME type
 */
export const getFileIcon = (mimeType: string): React.ReactNode => {
  const kind = kindFromMime(mimeType);
  if (kind === "image") return <FileImageOutlined />;
  if (kind === "audio") return <AudioOutlined />;
  if (kind === "video") return <PlayCircleOutlined />;
  // PDF
  if (mimeType === "application/pdf") {
    return <FilePdfOutlined />;
  }
  // Word documents
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return <FileWordOutlined />;
  }
  // PowerPoint
  if (mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
    return <FilePptOutlined />;
  }
  // Excel
  if (mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    return <FileExcelOutlined />;
  }
  // vCard contacts
  if (mimeType === "text/vcard") {
    return <ContactsOutlined />;
  }
  return <FileOutlined />;
};

/**
 * Formats bytes to human readable string (B, KB, MB)
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

/**
 * Validates if a file type is allowed
 */
export const isFileTypeAllowed = (
  mimeType: string,
  allowedTypes: string[] = WHATSAPP_ALLOWED_FILE_TYPES
): boolean => {
  return allowedTypes.includes(mimeType);
};

/**
 * Validates if a file size is within limit
 */
export const isFileSizeValid = (
  fileSize: number,
  maxSize: number = MAX_FILE_SIZE
): boolean => {
  return fileSize <= maxSize;
};
